import React, { createContext, useCallback, useContext, useEffect, useState, } from "react";
import CopyrightDialog from "@/react/Webshop/Components/CopyrightDialog";
import { PortalContext } from "@/react/Providers/PortalProvider";
import { useTranslation } from "react-i18next";
import { PREVIEW_PATHS, previewSelectors, } from "@/react/Webshop/previewSelectors";
const LONG_PRESS_MS = 420;
// Editing surfaces, such as the template crop editor, block without the
// message, as a dialog in the middle of a pan would interrupt the edit.
const SILENT_ATTRIBUTE = "data-image-save-protection-silent";
const isSilent = (target) => target instanceof Element &&
    target.closest(`[${SILENT_ATTRIBUTE}]`) !== null;
// A page can mount several React roots, each with its own provider, so the
// first listener to see an event claims it and the others skip it.
const CLAIMED = "epmsImageSaveProtectionClaimed";
const claim = (event) => {
    if (event[CLAIMED]) {
        return false;
    }
    event[CLAIMED] = true;
    return true;
};
const DOWNLOAD_PATH = "/system/xphoto/webshop/download";
const parsePreviewUrl = (url) => {
    try {
        const parsed = new URL(url, window.location.href);
        return parsed.origin === window.location.origin &&
            PREVIEW_PATHS.includes(parsed.pathname)
            ? parsed
            : null;
    }
    catch (_a) {
        return null;
    }
};
/** A preview img, or an element showing a preview as its own CSS background. */
const getPreviewUrl = (target) => {
    if (target instanceof HTMLImageElement) {
        return parsePreviewUrl(target.currentSrc || target.src);
    }
    if (!(target instanceof HTMLElement)) {
        return null;
    }
    const backgroundImage = window.getComputedStyle(target).backgroundImage;
    for (const match of backgroundImage.matchAll(/url\(\s*["']?([^"')]+)["']?\s*\)/g)) {
        const url = parsePreviewUrl(match[1]);
        if (url) {
            return url;
        }
    }
    return null;
};
const isPreviewElement = (target) => getPreviewUrl(target) !== null;
// The server picks the size and watermark; the effect and rotation follow the
// preview, so the copy looks like what the end user saw.
const getDownloadUrl = (target) => {
    const preview = getPreviewUrl(target);
    const uuid = preview === null || preview === void 0 ? void 0 : preview.searchParams.get("uuid");
    if (!preview || !uuid) {
        return null;
    }
    const params = new URLSearchParams({ uuid });
    for (const name of ["type", "rotation"]) {
        const value = preview.searchParams.get(name);
        if (value) {
            params.set(name, value);
        }
    }
    return `${DOWNLOAD_PATH}?${params}`;
};
// iOS Safari fires no contextmenu on a long press, so its save callout has to
// be switched off in CSS.
const previewImageCss = PREVIEW_PATHS.map((path) => `img[src*="${path}"]`).join(",\n") +
    ` {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}`;
// Same rule as WebshopPrintProtection::getPrintCss(), which XPage adds to
// legacy pages that mount no React root.
const printCss = previewSelectors.join(",\n") +
    ` {
  visibility: hidden !important;
}`;
const OverlayHandlerContext = createContext(() => { });
/**
 * For overlays that cover a preview without being one, such as the legacy
 * template editor's. Applies the portal's mode to the overlay's contextmenu.
 */
export const useOverlayContextMenuHandler = () => useContext(OverlayHandlerContext);
/**
 * Applies the portal's preview save protection to every webshop preview on
 * the page: right-click, long-press and drag are cancelled, and in "message"
 * and "download" mode the copyright dialog opens. In "download" mode it also
 * offers a small watermarked copy of the picture. On portals that hide
 * previews when printing, it also leaves them out of the printed page,
 * whatever the mode.
 */
const ImageSaveProtectionProvider = ({ children, }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const portal = useContext(PortalContext);
    const mode = (_b = (_a = portal === null || portal === void 0 ? void 0 : portal.webshop) === null || _a === void 0 ? void 0 : _a.imageSaveProtection) !== null && _b !== void 0 ? _b : "message";
    const hideWhenPrinting = (_d = (_c = portal === null || portal === void 0 ? void 0 : portal.webshop) === null || _c === void 0 ? void 0 : _c.hideImagesWhenPrinting) !== null && _d !== void 0 ? _d : false;
    const { i18n } = useTranslation();
    // i18next names languages "nb-NO", the portal config "nb_NO".
    const message = (_f = (_e = portal === null || portal === void 0 ? void 0 : portal.webshop) === null || _e === void 0 ? void 0 : _e.imageSaveMessages) === null || _f === void 0 ? void 0 : _f[i18n.language.replace("-", "_")];
    // downloadUrl is null when the dialog has no copy to offer, such as for an
    // overlay or in "message" mode.
    const [dialog, setDialog] = useState(null);
    const onOverlayContextMenu = useCallback((event) => {
        if (mode === "off") {
            return;
        }
        event.preventDefault();
        if (mode === "message" || mode === "download") {
            setDialog({ downloadUrl: null });
        }
    }, [mode]);
    useEffect(() => {
        if (mode === "off") {
            return;
        }
        const withMessage = mode === "message" || mode === "download";
        const openDialog = (target) => setDialog({
            downloadUrl: mode === "download" ? getDownloadUrl(target) : null,
        });
        // Android follows a long press with a contextmenu event, which finds the
        // dialog already open.
        let longPressTimer = null;
        const clearLongPress = () => {
            if (longPressTimer !== null) {
                window.clearTimeout(longPressTimer);
                longPressTimer = null;
            }
        };
        const onContextMenu = (event) => {
            if (!isPreviewElement(event.target) || !claim(event)) {
                return;
            }
            event.preventDefault();
            clearLongPress();
            if (withMessage && !isSilent(event.target)) {
                openDialog(event.target);
            }
        };
        const onDragStart = (event) => {
            if (event.target instanceof HTMLImageElement &&
                isPreviewElement(event.target) &&
                claim(event)) {
                event.preventDefault();
            }
        };
        const onTouchStart = (event) => {
            clearLongPress();
            if (isSilent(event.target) ||
                !isPreviewElement(event.target) ||
                !claim(event)) {
                return;
            }
            const target = event.target;
            longPressTimer = window.setTimeout(() => {
                longPressTimer = null;
                openDialog(target);
            }, LONG_PRESS_MS);
        };
        document.addEventListener("contextmenu", onContextMenu, true);
        document.addEventListener("dragstart", onDragStart, true);
        if (withMessage) {
            document.addEventListener("touchstart", onTouchStart, {
                capture: true,
                passive: true,
            });
            document.addEventListener("touchmove", clearLongPress, true);
            document.addEventListener("touchend", clearLongPress, true);
            document.addEventListener("touchcancel", clearLongPress, true);
        }
        return () => {
            clearLongPress();
            document.removeEventListener("contextmenu", onContextMenu, true);
            document.removeEventListener("dragstart", onDragStart, true);
            document.removeEventListener("touchstart", onTouchStart, true);
            document.removeEventListener("touchmove", clearLongPress, true);
            document.removeEventListener("touchend", clearLongPress, true);
            document.removeEventListener("touchcancel", clearLongPress, true);
        };
    }, [mode]);
    return (React.createElement(OverlayHandlerContext.Provider, { value: onOverlayContextMenu },
        children,
        mode !== "off" && React.createElement("style", null, previewImageCss),
        hideWhenPrinting && React.createElement("style", { media: "print" }, printCss),
        (mode === "message" || mode === "download") && (React.createElement(CopyrightDialog, { open: dialog !== null, downloadUrl: (_g = dialog === null || dialog === void 0 ? void 0 : dialog.downloadUrl) !== null && _g !== void 0 ? _g : undefined, message: message, onClose: () => setDialog(null) }))));
};
export default ImageSaveProtectionProvider;
